import React, { useState } from 'react';
import styled from 'styled-components';
import {
  AddressElementConfig,
  DEFAULT_ADDRESS_ELEMENTS,
} from '../CustomBlocks/AddressInput';
import { GripIcon, EyeIcon, EyeOffIcon } from '../shared/Icons';
import { ADMIN_COLORS } from '../../theme';

/* This editor's icons were drawn at 15px on a 24-viewBox stroke, smaller than
   the shared Icons.tsx default (20) — pin it explicitly per call. */
const ROW_ICON_SIZE = 15;

const COLORS = {
  border: ADMIN_COLORS.border,
  divider: ADMIN_COLORS.divider,
  text: ADMIN_COLORS.text,
  textSecondary: ADMIN_COLORS.textSecondary,
  muted: ADMIN_COLORS.textMuted,
  faint: ADMIN_COLORS.textFaint,
  accent: ADMIN_COLORS.accent,
  accentRing: ADMIN_COLORS.accentRing,
  hover: ADMIN_COLORS.hover,
  surface: ADMIN_COLORS.surface,
  disabledBg: ADMIN_COLORS.headBg,
};

const Wrapper = styled.div`
  margin-bottom: 20px;
`;

/* Matches the 12.5px/600 label + 8px gap used by every other control in the
   drawer, so this block sits on the same rhythm as the stock fields. */
const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  color: ${COLORS.textSecondary};
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.4;
`;

const Row = styled.div<{ $dragging: boolean; $over: boolean }>`
  display: flex;
  /* The middle column stacks two fields, so the grip and the Mandatory box
     pin to the first line rather than centring against both. */
  align-items: flex-start;
  gap: 8px;
  padding: 7px 0;
  border-bottom: 1px dashed ${COLORS.divider};
  opacity: ${(p) => (p.$dragging ? 0.4 : 1)};
  /* Drop indicator on the row being hovered during a drag. */
  box-shadow: ${(p) => (p.$over ? `inset 0 2px 0 ${COLORS.accent}` : 'none')};

  &:last-of-type {
    border-bottom: none;
  }
`;

const DragHandle = styled.span`
  display: flex;
  align-items: center;
  /* Match the label input's height so it centres against that field alone. */
  height: 38px;
  flex-shrink: 0;
  color: ${COLORS.faint};
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

const LabelInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 38px;
  padding: 0 34px 0 10px;
  border: 1px solid ${COLORS.border};
  border-radius: 8px;
  background: ${COLORS.surface};
  color: ${COLORS.text};
  font-size: 13px;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    border-color: ${COLORS.accent};
    box-shadow: 0 0 0 3px ${COLORS.accentRing};
  }

  &:disabled {
    background: ${COLORS.disabledBg};
    color: ${COLORS.faint};
  }
`;

const LabelWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
`;

/* Label and placeholder stack in the row's flexible middle column so the grip
   and the Mandatory box stay on the row's first line, aligned with the header. */
const FieldStack = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const PlaceholderInput = styled(LabelInput)`
  padding-right: 10px;
  font-size: 12.5px;

  &::placeholder {
    color: ${COLORS.faint};
  }
`;

/* Eye toggle lives inside the label field, mirroring the reference UI. */
const VisibilityToggle = styled.span<{ $hidden: boolean }>`
  position: absolute;
  right: 8px;
  display: flex;
  align-items: center;
  color: ${(p) => (p.$hidden ? COLORS.faint : COLORS.muted)};
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover {
    color: ${COLORS.text};
  }
`;

const MandatoryBox = styled.input`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  /* Centres against the label input (38px) that heads the stacked column. */
  margin: 11px 4px 0 0;
  accent-color: ${COLORS.accent};
  cursor: pointer;
`;

/* Same box, but this one sits beside a single-line label — no stacked column
   to offset against. */
const FooterBox = styled(MandatoryBox)`
  margin-top: 0;
`;

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
`;

const FooterLabel = styled.label`
  margin: 0;
  color: ${COLORS.text};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
`;

interface AddressElementsEditorProps {
  elements?: AddressElementConfig[];
  showElementsLabel: boolean;
  showIcon?: boolean;
  onChange: (elements: AddressElementConfig[]) => void;
  onShowElementsLabelChange: (show: boolean) => void;
  onShowIconChange?: (show: boolean) => void;
}

export const AddressElementsEditor: React.FC<AddressElementsEditorProps> = ({
  elements,
  showElementsLabel,
  showIcon = true,
  onChange,
  onShowElementsLabelChange,
  onShowIconChange,
}) => {
  const list =
    Array.isArray(elements) && elements.length ? elements : DEFAULT_ADDRESS_ELEMENTS;

  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const patch = (index: number, changes: Partial<AddressElementConfig>) => {
    onChange(list.map((el, i) => (i === index ? { ...el, ...changes } : el)));
  };

  const handleDrop = (targetIndex: number) => {
    if (dragIndex === null || dragIndex === targetIndex) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }
    const next = [...list];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(targetIndex, 0, moved);
    onChange(next);
    setDragIndex(null);
    setOverIndex(null);
  };

  return (
    <Wrapper>
      <HeaderRow>
        <span>Address Elements</span>
        <span>Mandatory</span>
      </HeaderRow>

      {list.map((el, index) => (
        <Row
          key={el.key}
          $dragging={dragIndex === index}
          $over={overIndex === index && dragIndex !== index}
          onDragOver={(e) => {
            e.preventDefault();
            setOverIndex(index);
          }}
          onDrop={() => handleDrop(index)}
        >
          <DragHandle
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData('text/plain', String(index));
              e.dataTransfer.effectAllowed = 'move';
              setDragIndex(index);
            }}
            onDragEnd={() => {
              setDragIndex(null);
              setOverIndex(null);
            }}
          >
            <GripIcon size={ROW_ICON_SIZE} />
          </DragHandle>

          <FieldStack>
            <LabelWrap>
              <LabelInput
                value={el.label}
                disabled={!el.visible}
                onChange={(e) => patch(index, { label: e.target.value })}
              />
              <VisibilityToggle
                $hidden={!el.visible}
                role="presentation"
                title={el.visible ? 'Hide this element' : 'Show this element'}
                onClick={() =>
                  patch(index, {
                    visible: !el.visible,
                    ...(el.visible ? { required: false } : {}),
                  })
                }
              >
                {el.visible ? (
                  <EyeIcon size={ROW_ICON_SIZE} />
                ) : (
                  <EyeOffIcon size={ROW_ICON_SIZE} />
                )}
              </VisibilityToggle>
            </LabelWrap>

            {/* Country renders as a <select> with its own "-Select-" option, so
                a placeholder would never show. */}
            {el.key !== 'country' && (
              <PlaceholderInput
                value={el.placeholder || ''}
                disabled={!el.visible}
                placeholder="Placeholder"
                onChange={(e) => patch(index, { placeholder: e.target.value })}
              />
            )}
          </FieldStack>

          <MandatoryBox
            type="checkbox"
            checked={Boolean(el.required)}
            disabled={!el.visible}
            onChange={(e) => patch(index, { required: e.target.checked })}
          />
        </Row>
      ))}

      <FooterRow>
        <FooterBox
          id="showElementsLabel"
          type="checkbox"
          checked={showElementsLabel}
          onChange={(e) => onShowElementsLabelChange(e.target.checked)}
        />
        <FooterLabel htmlFor="showElementsLabel">Show Elements Label</FooterLabel>
      </FooterRow>

      {onShowIconChange && (
        <FooterRow>
          <FooterBox
            id="showElementsIcon"
            type="checkbox"
            checked={showIcon}
            onChange={(e) => onShowIconChange(e.target.checked)}
          />
          <FooterLabel htmlFor="showElementsIcon">Show Icon</FooterLabel>
        </FooterRow>
      )}
    </Wrapper>
  );
};

export default AddressElementsEditor;
